import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { useScrollReveal } from '../hooks/useScrollReveal.js';
import { useNavTone } from '../hooks/useNavTone.js';
import { openApp } from '../lib/appUrl.js';
import heroGalleryTall from '../assets/BB/pexels-unlime-8181296.jpg';
import heroGalleryTop from '../assets/BB/pexels-keremkaplan-33124631.jpg';
import heroGalleryBottom from '../assets/BB/pexels-tomfisk-6594779.jpg';
import specsGalleryTall from '../assets/BB/pexels-maxed-raw-527582413-27207885.jpg';
import specsGalleryTop from '../assets/BB/pexels-sarthak-maji-2152979497-32567868.jpg';
import specsGalleryBottom from '../assets/BB/pexels-introspectivedsgn-35038788.jpg';
import './Billboard.css';

// billboard.html's own mobile-nav-links block — this page links to its own
// on-page sections rather than the generic cross-page list.
const MOBILE_LINKS = [
  { href: '/billboard#specs', label: 'Specs' },
  { href: '/billboard#value', label: 'Why FlipAd' },
  { href: '/billboard#pricing', label: 'Pricing' },
  { href: '/billboard#related', label: 'Other formats' },
  { href: '/billboard#faq', label: 'FAQ' },
];

// billboard.html's own inline nav-tone script section list.
const NAV_SECTIONS = [
  ['hero', 'dark'],
  ['specs', 'light'],
  ['pricing', 'light'],
  ['related', 'light'],
  ['faq', 'light'],
  ['cta', 'dark'],
];

const FAQ_ITEMS = [
  {
    q: 'What sizes of billboards are available?',
    a: 'Listed billboards typically range from 20×10 ft to 60×30 ft. Every listing states the exact dimensions, structure type, and facing direction — filter by size when you search.',
  },
  {
    q: 'How is billboard pricing calculated?',
    a: 'Space owners set their own monthly rate based on size, location, and footfall. Printing and installation are shown as separate line items so you always see the full cost before you book.',
  },
  {
    q: 'Can I book a billboard for less than a month?',
    a: 'Most billboards are booked monthly, but some space owners accept shorter flights for a premium. Check the availability calendar on each listing, or message the space owner directly for custom terms.',
  },
  {
    q: 'How do I know the billboard actually displayed my ad?',
    a: 'The space owner uploads a timestamped, geo-tagged photo within 48 hours of your creative going live. FlipAd verifies it before releasing your escrowed payment.',
  },
  {
    q: 'Can I combine billboards with other formats in one campaign?',
    a: 'Yes. Add billboards alongside digital screens, transit panels, or any other format to the same campaign plan, and manage every booking from a single dashboard.',
  },
];

// Mirrors billboard.html's FAQ accordion script: each item is its own
// collapsible block, with the parent driving "only one open at a time".
function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className={`so-faq-item${isOpen ? ' is-open' : ''}`}>
      <button className="so-faq-q" aria-expanded={isOpen} onClick={onToggle}>
        {item.q}
        <i className="ph ph-plus so-faq-icon" aria-hidden="true" />
      </button>
      <div className="so-faq-a"><div className="so-faq-a-inner">{item.a}</div></div>
    </div>
  );
}

export default function Billboard() {
  useScrollReveal();
  useNavTone(NAV_SECTIONS);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <Seo
        title="Billboard Advertising — Sizes, Pricing & Booking | FlipAd"
        description="Compare verified billboard listings across India by size, location, and price. See real dimensions and photos, and book with escrow-protected payment."
        path="/billboard"
      />
      <Header mobileLinks={MOBILE_LINKS} activeNavKey="billboard" />

      {/* ════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════ */}
      <section className="fmt-hero" id="hero" aria-labelledby="fmt-hero-hl">
        <div className="container">
          <div className="fmt-hero-inner">

            <div className="fmt-hero-left">
              <p className="fmt-breadcrumb">
                <Link to="/">Home</Link>
                <i className="ph ph-caret-right" style={{ fontSize: 9 }} />
                <Link to="/#formats">Formats</Link>
                <i className="ph ph-caret-right" style={{ fontSize: 9 }} />
                <span style={{ color: '#fff' }}>Billboard</span>
              </p>
              <h1 className="fmt-hero-hl" id="fmt-hero-hl">Billboards. Booked properly, for once.</h1>
              <p className="fmt-hero-sub">Large-format roadside displays with verified dimensions, transparent pricing, and escrow-protected payments — from search to timestamped proof of display.</p>
              <div className="fmt-hero-btns">
                <button type="button" className="lmn-btn primary lg nav-auth-trigger" onClick={() => openApp('/inventory')}>Browse billboard inventory <i className="ph ph-arrow-right" aria-hidden="true" /></button>
                <Link to="/space-owners" className="lmn-btn outline-white lg">List your billboard</Link>
              </div>
              <div className="fmt-hero-stats">
                <div><div className="fmt-hero-stat-val">480+</div><div className="fmt-hero-stat-lbl">Verified billboards</div></div>
                <div><div className="fmt-hero-stat-val">23</div><div className="fmt-hero-stat-lbl">Cities</div></div>
                <div><div className="fmt-hero-stat-val">₹2,000+/day</div><div className="fmt-hero-stat-lbl">Starting price</div></div>
              </div>
            </div>

            <div className="fmt-hero-gallery" aria-hidden="true">
              <div className="g-item tall" style={{ backgroundImage: `url(${heroGalleryTall})` }} />
              <div className="g-item" style={{ backgroundImage: `url(${heroGalleryTop})` }} />
              <div className="g-item" style={{ backgroundImage: `url(${heroGalleryBottom})` }} />
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          QUICK SPECS STRIP
      ════════════════════════════════════════════════ */}
      <div className="fmt-quickspecs">
        <div className="container">
          <div className="fmt-quickspecs-row">
            <div className="fmt-qs reveal" data-d="1">
              <div className="fmt-qs-icon"><i className="ph ph-ruler" aria-hidden="true" /></div>
              <div><div className="fmt-qs-val">20×10 – 60×30 ft</div><div className="fmt-qs-lbl">Standard size range</div></div>
            </div>
            <div className="fmt-qs reveal" data-d="2">
              <div className="fmt-qs-icon"><i className="ph ph-map-pin" aria-hidden="true" /></div>
              <div><div className="fmt-qs-val">Highways &amp; arterial roads</div><div className="fmt-qs-lbl">Best placement</div></div>
            </div>
            <div className="fmt-qs reveal" data-d="3">
              <div className="fmt-qs-icon"><i className="ph ph-currency-inr" aria-hidden="true" /></div>
              <div><div className="fmt-qs-val">₹2,000+ / day</div><div className="fmt-qs-lbl">Starting price</div></div>
            </div>
            <div className="fmt-qs reveal" data-d="4">
              <div className="fmt-qs-icon"><i className="ph ph-calendar-check" aria-hidden="true" /></div>
              <div><div className="fmt-qs-val">7–14 days</div><div className="fmt-qs-lbl">Avg. time to go live</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════
          WHY BILLBOARDS ON LUMEN
      ════════════════════════════════════════════════ */}
      <section className="fmt-value sec" id="value" aria-labelledby="value-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: 560 }}>
            <p className="eyebrow">Why book here</p>
            <h2 className="sec-title" id="value-hl">The same billboard. A lot less risk.</h2>
            <p className="sec-desc">Nothing about the medium changes — everything about the transaction does.</p>
          </div>

          <div className="fmt-value-grid">
            <div className="fmt-value-card reveal" data-d="1">
              <div className="fmt-value-icon"><i className="ph ph-receipt" aria-hidden="true" /></div>
              <h3 className="fmt-value-title">Transparent pricing</h3>
              <p className="fmt-value-desc">Every listing shows the exact dimensions, rate, and inclusions upfront — no blurry JPEGs, no back-and-forth over WhatsApp to find out what you're actually paying for.</p>
            </div>
            <div className="fmt-value-card reveal" data-d="2">
              <div className="fmt-value-icon"><i className="ph ph-map-pin-area" aria-hidden="true" /></div>
              <h3 className="fmt-value-title">Verified locations</h3>
              <p className="fmt-value-desc">Every site is inspected and geo-verified before it's listed, so you know exactly what you're booking, where it stands, and how it faces traffic.</p>
            </div>
            <div className="fmt-value-card reveal" data-d="3">
              <div className="fmt-value-icon"><i className="ph ph-lock-simple" aria-hidden="true" /></div>
              <h3 className="fmt-value-title">Escrow-protected payment</h3>
              <p className="fmt-value-desc">Your payment is held in escrow until the space owner submits timestamped, geo-tagged proof that your creative actually went up.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          SPECS + GALLERY
      ════════════════════════════════════════════════ */}
      <section className="sec" id="specs" aria-labelledby="specs-hl" style={{ background: 'var(--lumen-color-surface-raised)' }}>
        <div className="container">
          <div className="fmt-specs-layout">

            <div className="fmt-gallery reveal">
              <div className="fmt-gallery-item tall" style={{ backgroundImage: `url(${specsGalleryTall})` }} />
              <div className="fmt-gallery-item" style={{ backgroundImage: `url(${specsGalleryTop})` }} />
              <div className="fmt-gallery-item" style={{ backgroundImage: `url(${specsGalleryBottom})` }} />
            </div>

            <div className="reveal" data-d="2">
              <p className="eyebrow" style={{ color: 'var(--lumen-color-text-brand)' }}>Specifications</p>
              <h2 className="sec-title" id="specs-hl" style={{ marginBottom: 'var(--lumen-space-8)' }}>What you're actually booking.</h2>

              <div className="fmt-spec-row">
                <div className="fmt-spec-icon"><i className="ph ph-ruler" aria-hidden="true" /></div>
                <div>
                  <div className="fmt-spec-label">Standard sizes</div>
                  <div className="fmt-spec-val">20×10 ft to 60×30 ft</div>
                  <div className="fmt-spec-sub">Custom sizes available on request for landmark and high-visibility sites.</div>
                </div>
              </div>
              <div className="fmt-spec-row">
                <div className="fmt-spec-icon"><i className="ph ph-lightbulb" aria-hidden="true" /></div>
                <div>
                  <div className="fmt-spec-label">Structure types</div>
                  <div className="fmt-spec-val">Static print &amp; backlit / illuminated</div>
                  <div className="fmt-spec-sub">Filter by structure type on every listing before you book.</div>
                </div>
              </div>
              <div className="fmt-spec-row">
                <div className="fmt-spec-icon"><i className="ph ph-map-trifold" aria-hidden="true" /></div>
                <div>
                  <div className="fmt-spec-label">Best for</div>
                  <div className="fmt-spec-val">Highways, arterial roads, high-traffic junctions</div>
                </div>
              </div>
              <div className="fmt-spec-row">
                <div className="fmt-spec-icon"><i className="ph ph-calendar-blank" aria-hidden="true" /></div>
                <div>
                  <div className="fmt-spec-label">Booking term</div>
                  <div className="fmt-spec-val">Monthly, with multi-month discounts</div>
                </div>
              </div>
              <div className="fmt-spec-row">
                <div className="fmt-spec-icon"><i className="ph ph-camera" aria-hidden="true" /></div>
                <div>
                  <div className="fmt-spec-label">Proof of display</div>
                  <div className="fmt-spec-val">Timestamped, geo-tagged photo within 48 hrs of go-live</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          PRICING EXAMPLE
      ════════════════════════════════════════════════ */}
      <section className="fmt-pricing sec" id="pricing" aria-labelledby="pricing-hl">
        <div className="container">
          <div className="fmt-pricing-layout">

            <div className="reveal">
              <p className="eyebrow" style={{ color: 'var(--lumen-color-text-brand)' }}>Sample cost breakdown</p>
              <h2 className="sec-title" id="pricing-hl">What a booking actually costs.</h2>
              <p className="sec-desc">One illustrative 30×15 ft highway billboard, booked for 30 days. Actual pricing is set by each space owner and shown upfront on every listing — no surprises at checkout.</p>
            </div>

            <div className="fmt-price-card reveal" data-d="2">
              <div className="fmt-price-card-hd">
                <span className="fmt-price-card-title">MG Road, Bengaluru · 30 days</span>
                <i className="ph ph-signpost" style={{ color: 'var(--lumen-color-text-tertiary)' }} aria-hidden="true" />
              </div>
              <div className="fmt-price-row"><span className="lbl">Billboard rental</span><span className="val">₹1,20,000</span></div>
              <div className="fmt-price-row"><span className="lbl">Printing &amp; mounting</span><span className="val">₹18,000</span></div>
              <div className="fmt-price-row"><span className="lbl">Installation</span><span className="val">₹6,000</span></div>
              <div className="fmt-price-row"><span className="lbl">Platform fee</span><span className="val" style={{ color: 'var(--lumen-emerald-600)' }}>Free</span></div>
              <div className="fmt-price-row total"><span className="lbl">Held in escrow</span><span className="val">₹1,44,000</span></div>
              <p className="fmt-price-note"><i className="ph ph-info" aria-hidden="true" />Released to the space owner only after you receive timestamped proof of display.</p>
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          RELATED FORMATS
      ════════════════════════════════════════════════ */}
      <section className="fmt-related sec" id="related" aria-labelledby="related-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: 560 }}>
            <p className="eyebrow" style={{ color: 'var(--lumen-color-text-brand)' }}>Other formats</p>
            <h2 className="sec-title" id="related-hl">Not looking for a billboard?</h2>
          </div>

          <div className="fmt-related-grid reveal">
            <Link to="/billboard" className="fmt-related-card current">
              <div className="fmt-related-icon"><i className="ph ph-signpost" aria-hidden="true" /></div>
              <div><div className="fmt-related-name">Billboard</div><div className="fmt-related-tag">You're here</div></div>
            </Link>
            <Link to="/#formats" className="fmt-related-card">
              <div className="fmt-related-icon"><i className="ph ph-monitor-play" aria-hidden="true" /></div>
              <div><div className="fmt-related-name">Digital Screen</div><div className="fmt-related-tag">Malls &amp; metro stations</div></div>
            </Link>
            <Link to="/#formats" className="fmt-related-card">
              <div className="fmt-related-icon"><i className="ph ph-bus" aria-hidden="true" /></div>
              <div><div className="fmt-related-name">Transit Panel</div><div className="fmt-related-tag">Buses &amp; trains</div></div>
            </Link>
            <Link to="/#formats" className="fmt-related-card">
              <div className="fmt-related-icon"><i className="ph ph-broadcast" aria-hidden="true" /></div>
              <div><div className="fmt-related-name">Unipole Hoarding</div><div className="fmt-related-tag">Long highway approach</div></div>
            </Link>
            <Link to="/#formats" className="fmt-related-card">
              <div className="fmt-related-icon"><i className="ph ph-bench" aria-hidden="true" /></div>
              <div><div className="fmt-related-name">Street Furniture</div><div className="fmt-related-tag">Pedestrian footfall</div></div>
            </Link>
            <Link to="/#formats" className="fmt-related-card">
              <div className="fmt-related-icon"><i className="ph ph-buildings" aria-hidden="true" /></div>
              <div><div className="fmt-related-name">Commercial Spaces</div><div className="fmt-related-tag">Walls &amp; rooftops</div></div>
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          FAQ
      ════════════════════════════════════════════════ */}
      <section className="so-faq sec" id="faq" aria-labelledby="faq-hl">
        <div className="container">
          <div className="faq-layout">

            <div className="faq-main">
              <div className="sec-header reveal" style={{ maxWidth: 560, marginBottom: 'var(--lumen-space-10)' }}>
                <p className="eyebrow">Common questions</p>
                <h2 className="sec-title" id="faq-hl">Billboard bookings, answered.</h2>
              </div>

              <div className="so-faq-list reveal">
                {FAQ_ITEMS.map((item, i) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    isOpen={openFaq === i}
                    onToggle={() => setOpenFaq((cur) => (cur === i ? null : i))}
                  />
                ))}
              </div>
            </div>

            <aside className="faq-side reveal" data-d="2">
              <div className="faq-card">
                <span className="faq-card-badge"><i className="ph-fill ph-circle" aria-hidden="true" />Avg. reply under 2 hours</span>
                <h3 className="faq-card-title">Still have a question?</h3>
                <p className="faq-card-desc">Real people, not bots. Reach out and we'll walk you through pricing, escrow, or a specific site.</p>
                <div className="faq-card-actions">
                  <a href="mailto:support@flipad.in" className="lmn-btn secondary sm"><i className="ph ph-envelope-simple" aria-hidden="true" />Email support</a>
                  <a href="#" className="lmn-btn primary sm" onClick={(e) => e.preventDefault()}><i className="ph ph-whatsapp-logo" aria-hidden="true" />Chat on WhatsApp</a>
                </div>
              </div>
              <div className="faq-trust">
                <div className="faq-trust-item"><i className="ph ph-map-pin" aria-hidden="true" /><span><strong>480+</strong>&nbsp;verified billboards</span></div>
                <div className="faq-trust-item"><i className="ph ph-shield-check" aria-hidden="true" /><span><strong>100%</strong>&nbsp;escrow-protected</span></div>
                <div className="faq-trust-item"><i className="ph ph-buildings" aria-hidden="true" /><span><strong>23</strong>&nbsp;cities covered</span></div>
              </div>
            </aside>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          CLOSING CTA
      ════════════════════════════════════════════════ */}
      <section className="so-cta sec" id="cta" aria-labelledby="cta-hl">
        <div className="container">
          <div className="reveal">
            <p className="eyebrow">Get started</p>
            <h2 className="so-cta-hl" id="cta-hl">Ready to put your brand on a billboard?</h2>
            <p className="so-cta-sub">480+ verified billboards across 23 cities. Browse for free, book in minutes.</p>
            <div className="so-cta-btns">
              <button
                type="button"
                className="lmn-btn lg nav-auth-trigger"
                style={{ background: '#fff', borderColor: '#fff', color: 'var(--b900)' }}
                onClick={() => openApp('/inventory')}
              >
                Browse billboard inventory <i className="ph ph-arrow-right" aria-hidden="true" />
              </button>
            </div>
            <p className="so-cta-link">
              Have questions? <a href="mailto:support@flipad.in" style={{ color: 'color-mix(in srgb,#fff 60%,transparent)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>Contact us →</a>
            </p>
            <p className="so-cta-link" style={{ marginTop: 'var(--lumen-space-3)' }}>
              Own a billboard? <Link to="/space-owners" style={{ color: 'color-mix(in srgb,#fff 60%,transparent)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>List it on FlipAd →</Link>
            </p>
          </div>
        </div>
      </section>

      <Footer escrowHref="/advertisers#escrow" />
    </>
  );
}
